import { ActivityInfoWithStatus } from 'src/components/activity/LastActivityExecution';
import Activity from './activityInterfaces';
import { OperationStatus } from 'src/components/pipeline/PipelineBulkActionConfirmModal';

export interface BulkUpdateProgressModalProps {
	activities: ActivityInfoWithStatus[];
	activityStatuses: Record<number, OperationStatus>;
	errorMessages: Record<number, string>;
}

/** Display labels for each field group, used in the field selector UI */
export const FIELD_GROUP_LABELS = {
	schedule: 'Schedule',
	retry: 'Automatic Retry',
	alarm: 'Alarm',
	cluster: 'Cluster',
	iamRole: 'IAM Role',
} as const;

/** Field groups that can be bulk-edited */
export type FieldGroupKey = keyof typeof FIELD_GROUP_LABELS;
export type FieldGroupRecord = Partial<Record<FieldGroupKey, boolean>>;

/**
 * Controls how blank string fields in a section are applied during bulk edit.
 *
 * - KEEP (default): Blank fields are omitted from the API request, preserving each activity's current value.
 * - OVERWRITE: Blank fields are sent as empty strings, clearing existing values across all selected activities.
 *
 * Currently only the alarm section uses this, since it has free-form string fields (dedupe, login, CTI)
 * where customers may want to update some fields without clobbering others.
 */
export enum BlankFieldPolicy {
	KEEP = 'keep',
	OVERWRITE = 'overwrite',
}

export type BlankFieldPolicyRecord = Partial<Record<FieldGroupKey, BlankFieldPolicy>>;

/**
 * BulkUpdateFormValues derives directly from the Activity interface via Pick,
 * ensuring type-level consistency between the bulk edit form and the activity data model.
 * If the Activity interface changes, BulkUpdateFormValues will reflect those changes at compile time.
 *
 * Note: We Pick 'config' from Activity but narrow it to only IAM_ROLES because the full
 * Activity['config'] type includes ~50+ unrelated fields (SQL queries, S3 paths, booleans, etc.).
 * The narrowing still derives from Activity['config'], so type changes propagate at compile time.
 */
export interface BulkUpdateFormValues
	extends Pick<
		Activity,
		// Schedule
		| 'scheduleDate'
		| 'scheduleInterval'
		| 'scheduleType'
		// Retry
		| 'activityRetryActive'
		| 'activityRetryMaximum'
		| 'activityRetryDelay'
		| 'activityRetryPause'
		// Cluster
		| 'clusterName'
		// Alarm
		| 'alarm'
	> {
	/** Field group selection (checkboxes) — not part of Activity, specific to bulk edit UX */
	selectedFieldGroups: FieldGroupRecord;

	/**
	 * Per-section policy for how blank string fields are applied. Currently only `alarm` is wired up.
	 * KEEP (default) omits blank fields from the request; OVERWRITE sends them as empty strings.
	 */
	blankFieldPolicy: BlankFieldPolicyRecord;

	/**
	 * Narrowed from Activity['config'] to only IAM_ROLES — the only config field we bulk-edit.
	 * Still type-linked to Activity: if Activity['config']['IAM_ROLES'] changes, this breaks at compile time.
	 */
	config: Pick<
		Activity['config'],
		'IAM_ROLES' | 'DJS_ALARM_ALL_EXECUTIONS_FAILED' | 'DJS_ALARM_DISABLED_JOB' | 'DJS_ALARM_EXECUTION_FAILURE'
	>;
}
